// External libraries
// @ts-expect-error untyped-import
import { isBoolean } from '@checkout/react-components';

// Internal modules
import {
  features,
  isUserActionCommit,
  logger,
  ONBOARD_SUCCESS_PAYLOAD,
  STATE_NAME,
} from '@/lib';

import { appStore } from '@/store';
import { getGlobals } from '@/lib/globals';
import type { GenericObjectType } from '@/types';

type FOCUS_AND_BLUR_OPTIONS = {
  errorCode?: string;
  eventType?: string;
  fieldName?: string;
  isValid?: boolean;
};

type FOCUS_AND_BLUR_RETURN = {
  field_name?: string;
  int_error_code: '0' | '1';
  int_error_desc?: string;
  state_name: 'billing';
  transition_name: string;
};

export const FPTI_FOCUS_AND_BLUR_EVENT_PAYLOAD = (
  options: FOCUS_AND_BLUR_OPTIONS = {},
): FOCUS_AND_BLUR_RETURN => {
  const { errorCode, eventType, fieldName, isValid } = options;

  /**
   * Only form fields that we send to the API have their validity tracked
   * but some inputs that aren't sent to the API still use this function.
   * For those fields we will send the "validity" as "valid" for tracking
   * purposes. This way the payload is always the same and the information
   * is accurate -- if a field can't be invalid then it's always valid.
   * E.G. the ship to billing checkbox
   */
  const isValidLike = !isBoolean(isValid) || isValid;

  return {
    field_name: fieldName,
    int_error_code: isValidLike ? '0' : '1',
    int_error_desc: isValidLike ? '' : errorCode,
    state_name: 'billing',
    transition_name: `process_field_${eventType}`,
  };
};

const getCurrencyConversionPayload = () => {
  const { api: apiState, form: formState, ui: uiState } = appStore.getState();

  if (!uiState.showCardConversionOptions) {
    const { cartAmount: cart, griffinMetadata: buyer } = apiState;

    return {
      is_conversion_shown: 0,
      iscbr: cart?.currencyCode !== buyer.currencyCode, // is cross-border
    };
  }

  const { currencyConversionSelection } = formState;
  const { from, to } = apiState.currencyConversionData;

  return {
    currency_conversion_from_curr: from?.currencyCode,
    currency_conversion_selected: currencyConversionSelection,
    currency_conversion_to_curr: to?.currencyCode,
    is_conversion_shown: 1,
  };
};

export const logProcessBillingPageToFpti = (): void => {
  const { form: formState } = appStore.getState();
  const { value: onboardOption } = formState.onboardOption;
  const { value: shipToBilling } = formState.shipToBilling;
  const { value: autoCompleteType } =
    formState.shippingAddress.autoCompleteType;
  const { userAction } = getGlobals().initialGqlData.checkoutSession || {};

  logger.fpti('weasley_process_billing_page', {
    ship_to_billing: shipToBilling,
    shipping_address_collection: autoCompleteType,
    state_name: 'BILLING',
    transition_name: 'process_billing_page',
    user_action: userAction,
    user_opts_signup: onboardOption === 'signup' ? 'Y' : 'N',
    ...getCurrencyConversionPayload(),
  });
  logger.flush();
};

export const logOnboardSuccessToFpti = (
  onboardOption: keyof typeof STATE_NAME,
  fptiPerfLogStart: number,
): void => {
  const payload = ONBOARD_SUCCESS_PAYLOAD({
    mode: onboardOption,
    startTime: fptiPerfLogStart,
  });

  logger.fpti(`onboard_${onboardOption}_success`, payload);
};

export const logPayLaterLearnMoreClickedToFpti = (): void => {
  logger.fpti('pay_later_learn_more_clicked', {
    event_name: 'guest_pay_later_learn_more_clicked',
    state_name: STATE_NAME.billing,
    transition_name: 'guest_pay_later_learn_more',
  });
};

export const logPayLaterTermsCheckedToFpti = (): void => {
  logger.fpti('pay_later_tc_checked', {
    event_name: 'guest_pay_later_tc_checked',
    state_name: STATE_NAME.billing,
    transition_name: 'guest_pay_later_tc_checked',
  });
};

export const logPayLaterContinueButtonSubmittedToFpti = (): void => {
  logger.fpti('pay_later_continue_button_submitted', {
    event_name: 'pay_later_continue_button_submitted',
    state_name: 'bnpl_guest_review',
    transition_name: 'process_guest_pay_later',
  });
};

export const logGuestToMemberTransitionToFpti = (
  options: GenericObjectType,
): void => {
  const { status } = options;

  logger.fpti(`guest_to_member_transition_${status}`, {
    event_name: 'guest_to_member_transition',
    state_name: 'guest_xo',
    status,
    transition_name: 'guest_to_member_transition',
    ...options,
  });
};

/**
 * Logging for Form tabs
 */
export const logPrepareTabbedViewToFpti = (
  options: GenericObjectType,
): void => {
  logger.fpti('prepare_tabbed_view', {
    event_name: 'xolite_guest_tabbed_view_shown',
    state_name: STATE_NAME.billing,
    transition_name: 'prepare_tabbed_view',
    ...options,
  });
};

export const logFiTabClickEventToFpti = (tabName: string): void => {
  logger.fpti(`${tabName}_tab_clicked`, {
    event_name: 'guest_tab_clicked',
    state_name: STATE_NAME.billing,
    transition_name: `select_guest_${tabName}`,
  });
};

/**
 * @function getHideOvercaptureReason
 * @param {string} overcaptureAmount overcapture amount value
 * @returns {string} Returns the hide overcapture reason if any
 */
const getHideOvercaptureReason = (overcaptureAmount: string) => {
  if (!overcaptureAmount) {
    return 'edm_est_amount_null';
  }
  return '';
};

export const getOvercaptureFPTILogKey = ({
  isApprove,
  showOvercapture,
}: {
  isApprove: boolean;
  showOvercapture: boolean;
}): string => {
  const userAction = isUserActionCommit() ? 'PayNow' : 'Continue';
  return `Overcapture_${isApprove ? 'Approve_' : ''}${
    showOvercapture ? '' : 'No_'
  }Disclaimer_${userAction}_Integration`;
};

export const logOvercapturesWhenRendering = (
  overcaptureAmount: string,
  showOvercapture: boolean,
): void => {
  const { cart, merchant } = getGlobals().initialGqlData.checkoutSession || {
    merchant: {},
  };
  const cartAmount = cart?.amounts?.total?.currencyFormatSymbolISOCurrency;

  const defaultLogPayload = {
    edm_est_amt: overcaptureAmount,
    initial_amt: cartAmount,
    merchant_acc_num: merchant.merchantId,
    merchant_country: merchant.country,
  };

  const fptiPayload = showOvercapture
    ? defaultLogPayload
    : {
        ...defaultLogPayload,
        reason: getHideOvercaptureReason(overcaptureAmount),
      };

  logger.info(
    getOvercaptureFPTILogKey({
      isApprove: false,
      showOvercapture,
    }),
    fptiPayload,
  );
};

export const logOverCapturesDataOnSubmit = (): void => {
  const { isOverCaptureVisible } = appStore.getState().ui;

  logger.info(
    getOvercaptureFPTILogKey({
      isApprove: true,
      showOvercapture: isOverCaptureVisible,
    }),
  );
};

/**
 * @function logStretchRewardsEligibilityToFpti
 * @summary Calls an FPTI logger to record the
 * result of the isExpandedStretchRewards resolver.
 * provides an ineligibility reason if ineligible.
 * @returns {void} Does not return anything.
 */
export const logStretchRewardsEligibilityToFpti = (): void => {
  let ineligibilityReason = 'stretch_reward_ineligible';
  const isExpandedStretchRewards = features.isActive(
    'isExpandedStretchRewards',
  );
  const isExpandedStretchRewardsIneligibleDownstream = features.isActive(
    'isExpandedStretchRewardsIneligibleDownstream',
  );

  if (isExpandedStretchRewardsIneligibleDownstream) {
    ineligibilityReason = 'xobuyer_stretch_reward_flagged_ineligible';
  } else if (!isExpandedStretchRewards) {
    ineligibilityReason = 'elmo_stretch_reward_trmt_ineligible';
  }

  logger.fpti('stretch_rewards_eligibility', {
    eligibility: isExpandedStretchRewards ? 'true' : 'false',
    event_name: 'stretch_rewards_eligibility',
    experiment_name: 'FEATURE_EXPAND_VALUE_PROPS',
    ineligibility_reason: ineligibilityReason,
  });
};

/**
 * @function logStretchRewardsAttachedToFpti
 * @summary Calls an FPTI logger recording if we attempt
 * to enroll the user in stretch rewards on form submission
 * @param {boolean} eligibility Boolean result of isExpandedStretchRewards resolver.
 * @returns {void} Does not return anything.
 */
export const logStretchRewardsAttachedToFpti = (eligibility: boolean): void => {
  logger.fpti('stretch_rewards_attach', {
    eligibility: eligibility ? 'true' : 'false',
    event_name: 'stretch_rewards_attach',
    experiment_name: 'FEATURE_EXPAND_VALUE_PROPS',
    is_rewards_linked: eligibility ? 'Y' : 'N',
  });
};

/**
 * @function logBankPaymentSuppressionErrorToFpti
 * @summary Logs FPTI event when bank payment suppression error is shown
 * (user switches to bank tab while bank payments are suppressed)
 */
export const logBankPaymentSuppressionErrorToFpti = (): void => {
  logger.fpti('bank_payment_suppression_error_shown', {
    event_name: 'guest_bank_payment_suppression_error_shown',
    experiment_name: 'FEATURE_SUPPRESS_BANK_FUNDED_GUEST_PAYMENTS',
    state_name: STATE_NAME.billing,
    transition_name: 'bank_payment_suppression_error_shown',
  });
};

/**
 * @function logBankPaymentSuppressionActiveToFpti
 * @summary Logs FPTI event when bank payment suppression feature is active
 * (card tab is defaulted instead of bank tab)
 * @param {string} defaultTab - The default tab that was selected
 * @returns {void}
 */
export const logBankPaymentSuppressionActiveToFpti = (
  defaultTab: string,
): void => {
  logger.fpti('bank_payment_suppression_active', {
    default_tab: defaultTab,
    event_name: 'guest_bank_payment_suppression_active',
    experiment_name: 'FEATURE_SUPPRESS_BANK_FUNDED_GUEST_PAYMENTS',
    state_name: STATE_NAME.billing,
    transition_name: 'prepare_bank_payment_suppression',
  });
};

/**
 * @function logBankPaymentSuppressionSubmitBlockedToFpti
 * @summary Logs FPTI event when form submission is blocked due to
 * bank payment suppression (user tried to submit with bank tab active)
 * @returns {void}
 */
export const logBankPaymentSuppressionSubmitBlockedToFpti = (): void => {
  logger.fpti('bank_payment_suppression_submit_blocked', {
    event_name: 'guest_bank_payment_suppression_submit_blocked',
    experiment_name: 'FEATURE_SUPPRESS_BANK_FUNDED_GUEST_PAYMENTS',
    state_name: STATE_NAME.billing,
    transition_name: 'process_bank_payment_suppression_submit_blocked',
  });
};

export const logMiddleNameProvidedToFpti = (): void => {
  logger.fpti('middle_name_provided', {
    event_name: 'guest_middle_name_provided',
    state_name: STATE_NAME.billing,
    transition_name: 'middle_name_provided',
  });
};

/**
 * @function logPayLaterFormRender
 * @summary Logs FPTI event when Pay Later form is shown
 */
export const logPayLaterFormRender = ({
  buyerCountry,
  isGooglePayLaterOnly,
  merchantCountry,
}: {
  buyerCountry: string;
  isGooglePayLaterOnly: boolean;
  merchantCountry: string;
}): void => {
  const { flags, userAction } =
    getGlobals().initialGqlData.checkoutSession || {};
  const { payLaterOnly } = flags || {};

  logger.fpti('pay_later_form_shown', {
    buyer_country: buyerCountry,
    event_name: 'pay_later_form_shown',
    is_google_pay_later_only: isGooglePayLaterOnly?.toString() ?? 'UNKNOWN',
    merchant_country: merchantCountry,
    pay_later_only: payLaterOnly?.toString() ?? 'UNKNOWN',
    state_name: 'pay_later_form',
    transition_name: 'pay_later_form_shown',
    user_action: userAction,
  });
};

/**
 * @function logGooglePayLaterCountryOverride
 * @summary Log event when locale country is not in
 * GOOGLE_PAY_LATER_COUNTRIES and country is overridden
 */
export const logGooglePayLaterCountryOverride = ({
  buyerCountry,
  merchantCountry,
  toCountry,
}: {
  buyerCountry: string;
  merchantCountry: string;
  toCountry: string;
}): void => {
  const defaultedTo =
    toCountry === merchantCountry ? 'merchant_country' : toCountry;

  logger.fpti('google_pay_later_country_override', {
    buyer_country: buyerCountry,
    defaulted_to: defaultedTo,
    event_name: 'google_pay_later_country_override',
    merchant_country: merchantCountry,
    state_name: 'google_pay_later_country_override',
    to_country: toCountry,
    transition_name: 'google_pay_later_country_override',
  });
};
